import { db } from './lib/supabase';
import { getCurrentUser } from './lib/supabase';
import type { SyncAdapter } from './sync';

interface SyncPayload {
  [key: string]: unknown;
}

const entityMethods = {
  products: 'products',
  news: 'news',
  gallery: 'gallery',
  partners: 'partners',
  team_members: 'team',
  users: 'users',
  inquiries: 'inquiries',
  messages: 'contactMessages',
} as const;

function contentPayload(payload: unknown): SyncPayload {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }

  const { id: _id, created_at: _createdAt, updated_at: _updatedAt, ...content } = payload as SyncPayload;
  return content;
}

async function normalizeNewsPayload(entity: string, payload: SyncPayload) {
  if (entity !== 'news' || payload.author_id !== 'local-admin') return payload;
  const user = await getCurrentUser();
  if (!user) throw new Error('Sign in again before pushing news to the customer website.');
  return { ...payload, author_id: user.id };
}

function errorCode(error: unknown): string {
  return error && typeof error === 'object' && 'code' in error
    ? String((error as { code?: unknown }).code)
    : '';
}

async function reconcileBySlug(entity: string, payload: SyncPayload, repository: {
  create?: (payload: SyncPayload) => Promise<unknown>;
  update?: (id: string, payload: SyncPayload) => Promise<unknown>;
}) {
  const slug = typeof payload.slug === 'string' ? payload.slug : '';
  const lookup = entity === 'products' ? db.products.getBySlug : entity === 'news' ? db.news.getBySlug : null;
  if (!lookup || !slug || !repository.update) {
    if (repository.create) await repository.create(payload);
    return;
  }

  let remote: { id: string } | null = null;
  try {
    remote = await lookup(slug) as unknown as { id: string } | null;
  } catch (error) {
    if (errorCode(error) !== 'PGRST116') throw error;
  }
  if (remote) {
    await repository.update(remote.id, payload);
    return;
  }
  if (repository.create) await repository.create(payload);
}

export function createSupabaseSyncAdapter(): SyncAdapter {
  return {
    async apply(operation) {
      const methodName = entityMethods[operation.entity as keyof typeof entityMethods];
      if (!methodName) {
        throw new Error(`Unsupported sync entity: ${operation.entity}`);
      }

      const repository = (db as unknown as Record<string, {
        create?: (payload: SyncPayload) => Promise<unknown>;
        update?: (id: string, payload: SyncPayload) => Promise<unknown>;
        delete?: (id: string) => Promise<void>;
      }>)[methodName];

      if (!repository) {
        throw new Error(`Supabase repository unavailable: ${methodName}`);
      }

      if (operation.operation === 'create') {
        if (!repository.create) throw new Error(`Create is not supported for ${operation.entity}`);
        const payload = await normalizeNewsPayload(operation.entity, contentPayload(operation.payload));
        try {
          await repository.create(payload);
        } catch (error) {
          if (errorCode(error) !== '23505') throw error;
          await reconcileBySlug(operation.entity, payload, repository);
        }
        return;
      }

      if (operation.operation === 'update') {
        if (!repository.update) throw new Error(`Update is not supported for ${operation.entity}`);
        const payload = await normalizeNewsPayload(operation.entity, contentPayload(operation.payload));
        try {
          await repository.update(operation.entity_id, payload);
        } catch (error) {
          if (errorCode(error) !== 'PGRST116') throw error;
          await reconcileBySlug(operation.entity, payload, repository);
        }
        return;
      }

      if (!repository.delete) throw new Error(`Delete is not supported for ${operation.entity}`);
      await repository.delete(operation.entity_id);
    },
  };
}
